import * as Y from 'yjs';
import { canvasRoots, type CanvasBinding, TOP_LEVEL_BINDING } from '../../wiring/canvas-roots';

/**
 * Z-order helpers — operate on the canvas's single `'zOrder'` Y.Array<string>
 * which holds top-level node ids AND edge ids interleaved in render order.
 * Last entry renders on top; first renders behind everything.
 *
 * Edge ids and node ids share one stack: the same way Miro/FigJam treat
 * connectors as elements you can re-order against shapes. The backend
 * canvas-observer's `generate_summary` walks `zOrder` and skips ids that
 * don't resolve to a node (extract.rs:175), so edge ids in this array
 * are inert from the embedding pipeline's perspective.
 */

export function bringToFront(doc: Y.Doc, clientId: number, id: string, binding: CanvasBinding = TOP_LEVEL_BINDING): void {
  const arr = canvasRoots(doc, binding).zOrder;
  doc.transact(() => {
    const items = arr.toArray();
    const idx = items.indexOf(id);
    if (idx !== -1 && idx !== items.length - 1) {
      arr.delete(idx, 1);
      arr.push([id]);
    }
  }, clientId);
}

export function sendToBack(doc: Y.Doc, clientId: number, id: string, binding: CanvasBinding = TOP_LEVEL_BINDING): void {
  const arr = canvasRoots(doc, binding).zOrder;
  doc.transact(() => {
    const items = arr.toArray();
    const idx = items.indexOf(id);
    if (idx > 0) {
      arr.delete(idx, 1);
      arr.insert(0, [id]);
    }
  }, clientId);
}

export function bringForward(doc: Y.Doc, clientId: number, id: string, binding: CanvasBinding = TOP_LEVEL_BINDING): void {
  const arr = canvasRoots(doc, binding).zOrder;
  doc.transact(() => {
    const items = arr.toArray();
    const idx = items.indexOf(id);
    if (idx !== -1 && idx < items.length - 1) {
      arr.delete(idx, 1);
      arr.insert(idx + 1, [id]);
    }
  }, clientId);
}

export function sendBackward(doc: Y.Doc, clientId: number, id: string, binding: CanvasBinding = TOP_LEVEL_BINDING): void {
  const arr = canvasRoots(doc, binding).zOrder;
  doc.transact(() => {
    const items = arr.toArray();
    const idx = items.indexOf(id);
    if (idx > 0) {
      arr.delete(idx, 1);
      arr.insert(idx - 1, [id]);
    }
  }, clientId);
}

/**
 * One-shot migration: if any ids exist in the legacy `'edgeZOrder'` array,
 * append them onto `'zOrder'` (preserving relative edge order) and clear
 * `'edgeZOrder'`. Idempotent — re-running on a migrated doc is a no-op.
 *
 * Runs at read time on the client (called from useEdgesFromYjs)
 * so canvases authored under the two-array world heal in place. Backend
 * extract.rs already tolerates unknown ids in zOrder (extract.rs:175 plus
 * an explicit "skip zombie ids" test in canvas_observer_test.rs:593), so
 * folding edge ids in is safe to deploy without an API change.
 */
export function migrateLegacyEdgeZOrder(doc: Y.Doc, clientId: number, binding: CanvasBinding = TOP_LEVEL_BINDING): void {
  // Guard on the legacy `edgeZOrder` array ALONE — do NOT resolve the full
  // canvasRoots here. The full resolver also first-touches the `zOrder` type,
  // and a lazy type-registration outside the transact fires an extra
  // null-origin transaction (breaking the byte-identical single-transaction
  // contract). We resolve the full roots INSIDE the transact below so the
  // `zOrder` first-touch lands within the clientId-tagged transaction, exactly
  // where the legacy inline `doc.getArray('zOrder')` access placed it.
  const legacy = legacyEdgeZOrder(doc, binding);
  if (legacy.length === 0) return;
  doc.transact(() => {
    const ids = legacy.toArray();
    if (ids.length === 0) return;
    const { zOrder: main } = canvasRoots(doc, binding);
    // Skip ids already present in zOrder (e.g. partial migration from an
    // earlier session) so we don't duplicate.
    const existing = new Set(main.toArray());
    const toAppend = ids.filter((id) => !existing.has(id));
    if (toAppend.length > 0) main.push(toAppend);
    legacy.delete(0, legacy.length);
  }, clientId);
}

/**
 * Resolve ONLY the legacy `edgeZOrder` array for the given binding — without
 * touching the other canvas roots. Used by the migration guard so the
 * zOrder first-touch can be deferred into the tagged transaction.
 */
function legacyEdgeZOrder(doc: Y.Doc, binding: CanvasBinding): Y.Array<string> {
  if (binding.kind === 'nested') {
    const parent = doc.getMap<unknown>(binding.parentKey);
    let a = parent.get('edgeZOrder') as Y.Array<string> | undefined;
    if (!a) {
      a = new Y.Array<string>();
      parent.set('edgeZOrder', a);
    }
    return a;
  }
  return doc.getArray<string>('edgeZOrder');
}
